import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  Statistic,
  Spin,
  message,
  Select,
  Table,
  Button,
  Alert,
  DatePicker,
} from "antd";
import {
  UserOutlined,
  CarOutlined,
  TeamOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import dayjs from "../../../utils/dayjs";
import api from "../../../axiosConfig";
import axios from "axios";
import "./Dashboard.css";

const { Option } = Select;
const { RangePicker } = DatePicker;

const COLORS = {
  Programado: "#6c06b5",
  "En Curso": "#FFBF00",
  Finalizado: "#4d4d4d",
  Cancelado: "#b10e1a",
  Fallido: "#4a2c04",
  "Pendiente Validación": "#fa8c16",
};

const FALLBACK_DATA = {
  stats: {
    totalPacientes: 0,
    totalConductores: 0,
    totalClientes: 0,
    trasladosHoy: 0,
    trasladosCompletados: 0,
    trasladosCancelados: 0,
    trasladosFallidos: 0,
    trasladosProgramados: 0,
    trasladosEnCurso: 0,
    trasladosPendienteValidacion: 0,
  },
  trasladosPorEstado: [],
  topClientes: [],
};

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(FALLBACK_DATA.stats);
  const [trasladosPorEstado, setTrasladosPorEstado] = useState(
    FALLBACK_DATA.trasladosPorEstado
  );
  const [topClientes, setTopClientes] = useState(FALLBACK_DATA.topClientes);
  const [rangoFecha, setRangoFecha] = useState("hoy");
  const [apiError, setApiError] = useState(false);
  const [errorDetails, setErrorDetails] = useState("");
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [customDateRange, setCustomDateRange] = useState([
    dayjs().subtract(1, "month"),
    dayjs(),
  ]);

  // Función para mapear los nombres de estado
  const mapEstadoToOriginal = (estado) => {
    switch (estado) {
      case "trasladosProgramados":
        return "Programado";
      case "trasladosEnCurso":
        return "En Curso";
      case "trasladosCompletados":
        return "Finalizado";
      case "trasladosCancelados":
        return "Cancelado";
      case "trasladosFallidos":
        return "Fallido";
      default:
        return estado;
    }
  };

  // Procesar datos para las gráficas con porcentajes exactos
  const processedTrasladosPorEstado = useCallback(() => {
    const total = trasladosPorEstado.reduce(
      (sum, item) => sum + (item.count || 0),
      0
    );

    return trasladosPorEstado.map((item) => {
      const estado = mapEstadoToOriginal(item.estado);
      const count = item.count || 0;
      const porcentaje = total > 0 ? (count / total) * 100 : 0;

      return {
        estado,
        count,
        porcentaje: Number(porcentaje.toFixed(1)), // 1 decimal
      };
    });
  }, [trasladosPorEstado]);

  // Calcular el total para mostrar porcentajes exactos
  const totalTraslados = useCallback(() => {
    return processedTrasladosPorEstado().reduce(
      (sum, item) => sum + item.count,
      0
    );
  }, [processedTrasladosPorEstado]);

  const getRangoFechas = useCallback(
    (rango) => {
      const hoy = dayjs();

      switch (rango) {
        case "personalizado":
          return {
            inicio: customDateRange[0].startOf("day"),
            fin: customDateRange[1].endOf("day"),
          };
        case "hoy":
          return { inicio: hoy.startOf("day"), fin: hoy.endOf("day") };
        case "ayer":
          return {
            inicio: hoy.subtract(1, "day").startOf("day"),
            fin: hoy.subtract(1, "day").endOf("day"),
          };
        case "mañana":
          return {
            inicio: hoy.add(1, "day").startOf("day"),
            fin: hoy.add(1, "day").endOf("day"),
          };
        case "semana":
          return { inicio: hoy.startOf("week"), fin: hoy.endOf("week") };
        case "mes":
          return { inicio: hoy.startOf("month"), fin: hoy.endOf("month") };
        case "mes_anterior":
          return {
            inicio: hoy.subtract(1, "month").startOf("month"),
            fin: hoy.subtract(1, "month").endOf("month"),
          };
        default:
          return { inicio: hoy.startOf("day"), fin: hoy.endOf("day") };
      }
    },
    [customDateRange]
  );

  const fetchDashboardData = useCallback(
    async (rango) => {
      let inicio, fin;

      if (rango === "personalizado") {
        inicio = customDateRange[0].startOf("day");
        fin = customDateRange[1].endOf("day");
      } else {
        const fechas = getRangoFechas(rango);
        inicio = fechas.inicio;
        fin = fechas.fin;
      }

      const fechaInicioStr = inicio.format("YYYY-MM-DD");
      const fechaFinStr = fin.format("YYYY-MM-DD");

      try {
        setLoading(true);
        setApiError(false);
        setErrorDetails("");

        const response = await api.get("/dashboard/stats", {
          params: { fechaInicio: fechaInicioStr, fechaFin: fechaFinStr },
          timeout: 15000,
        });

        if (response.data && typeof response.data === "object") {
          const { stats, trasladosPorEstado, topClientes } = response.data;
          setStats(stats || FALLBACK_DATA.stats);
          setTrasladosPorEstado(trasladosPorEstado || []);
          setTopClientes(topClientes || []);
        } else {
          throw new Error("Formato de respuesta inválido");
        }
      } catch (error) {
        let errorMessage = "Error desconocido";
        if (error.response) {
          errorMessage = `Error ${error.response.status}: ${
            error.response.data?.error || error.response.statusText
          }`;
        } else if (error.request) {
          errorMessage = "No se recibió respuesta del servidor";
        } else {
          errorMessage = error.message;
        }

        try {
          const baseUrl = `${window.location.protocol}//${window.location.hostname}`;
          const port = process.env.NODE_ENV === "development" ? ":3001" : "";
          const alternativeUrl = `${baseUrl}${port}/api/dashboard/stats`;

          const backupResponse = await axios.get(alternativeUrl, {
            params: { fechaInicio: fechaInicioStr, fechaFin: fechaFinStr },
            timeout: 10000,
            headers: {
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
          });

          if (backupResponse.data && typeof backupResponse.data === "object") {
            const { stats, trasladosPorEstado, topClientes } =
              backupResponse.data;
            setStats(stats || FALLBACK_DATA.stats);
            setTrasladosPorEstado(trasladosPorEstado || []);
            setTopClientes(topClientes || []);
          } else {
            throw new Error("Formato de respuesta inválido (URL alternativa)");
          }
        } catch (backupError) {
          setStats(FALLBACK_DATA.stats);
          setTrasladosPorEstado(FALLBACK_DATA.trasladosPorEstado);
          setTopClientes(FALLBACK_DATA.topClientes);
          setApiError(true);
          setErrorDetails(errorMessage);
          message.error("No se pudieron cargar los datos del dashboard");
        }
      } finally {
        setLoading(false);
      }
    },
    [customDateRange, getRangoFechas]
  );

  useEffect(() => {
    if (rangoFecha !== "personalizado") {
      fetchDashboardData(rangoFecha);
    }
  }, [rangoFecha, fetchDashboardData]);

  const handleRangoChange = (value) => {
    setRangoFecha(value);
    setShowCustomPicker(value === "personalizado");
    if (value !== "personalizado") {
    setCustomDateRange([]);
    fetchDashboardData(value); 
  }
  };

  const handleCustomRangeChange = (dates) => {
    if (!dates || dates.length !== 2) {
      return;
    }

    const [start, end] = dates;

    // Validación de máximo 2 meses
    const diffMonths = end.diff(start, "month", true); // true = decimal (más preciso)
    if (diffMonths > 2) {
      message.error("El rango no puede ser mayor a 2 meses");
      return;
    }

    setCustomDateRange(dates);
  };

  const handleRefresh = () => {
    fetchDashboardData(rangoFecha);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="custom-tooltip">
          <p className="label">{`${data.estado}`}</p>
          <p className="value">{`${data.count} traslados`}</p>
          <p className="percentage">{`${data.porcentaje}%`}</p>
        </div>
      );
    }
    return null;
  };

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    payload,
  }) => {
    if (payload.porcentaje === 0) return null;

    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    // Usar el porcentaje calculado exacto
    const percentage = payload.porcentaje;
    const fontSize = percentage < 5 ? 10 : 12;

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        fontSize={fontSize}
      >
        {`${percentage}%`}
      </text>
    );
  };

  const columnsTopClientes = [
    {
      title: "Cliente EPS",
      dataIndex: "cliente",
      key: "cliente",
      render: (text) => (
        <div className="cliente-cell">
          <TeamOutlined
            style={{ fontSize: "20px", color: "#1890ff", marginRight: "8px" }}
          />
          <span className="cliente-nombre">{text}</span>
        </div>
      ),
    },
    {
      title: "Total Traslados",
      dataIndex: "count",
      key: "count",
      align: "right",
      render: (count) => (
        <div className="count-cell">
          <span className="count-value">{count}</span>
          <span className="count-label">traslados</span>
        </div>
      ),
    },
  ];

  // Datos procesados para gráficas
  const chartData = processedTrasladosPorEstado();

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Control de Traslados</h1>
          <p>Monitoreo de traslados</p>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <Select
            className="date-selector"
            value={rangoFecha}
            onChange={handleRangoChange}
            disabled={loading}
          >
            <Option value="hoy">Hoy</Option>
            <Option value="ayer">Ayer</Option>
            <Option value="mañana">Mañana</Option>
            <Option value="semana">Esta Semana</Option>
            <Option value="mes">Este Mes</Option>
            <Option value="mes_anterior">Mes Anterior</Option>
            <Option value="personalizado">Personalizado</Option>
          </Select>

          {showCustomPicker && (
            <div
              style={{ display: "flex", alignItems: "center", marginLeft: 10 }}
            >
              <RangePicker
                value={customDateRange}
                onChange={handleCustomRangeChange}
                format="DD/MM/YYYY"
                disabledDate={(current) => {
                  return current && current > dayjs().endOf("day");
                }}
              />
              <Button
                type="primary"
                style={{ marginLeft: 10 }}
                onClick={() => fetchDashboardData("personalizado")}
                disabled={loading}
              >
                Aplicar
              </Button>
            </div>
          )}

          {rangoFecha !== "personalizado" && (
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
              style={{ marginLeft: "10px" }}
            >
              Actualizar
            </Button>
          )}
        </div>
      </div>

      {apiError && (
        <Alert
          message="Error de conexión con el servidor"
          description={
            <>
              <p>
                No se pudieron cargar los datos completos del dashboard. Se
                están mostrando datos limitados.
              </p>
              {errorDetails && (
                <p>
                  <strong>Detalles del error:</strong> {errorDetails}
                </p>
              )}
            </>
          }
          type="warning"
          showIcon
          style={{ marginBottom: "24px" }}
          action={
            <Button size="small" type="primary" onClick={handleRefresh}>
              Reintentar
            </Button>
          }
        />
      )}

      {loading ? (
        <div className="loading-container">
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Row gutter={[24, 24]} className="main-stats">
            <Col xs={24} md={12}>
              <Row gutter={[24, 24]} style={{ height: "100%" }}>
                <Col xs={24} sm={12}>
                  <Card className="stat-card">
                    <Statistic
                      title="Total Pacientes"
                      value={stats.totalPacientes}
                      prefix={<UserOutlined />}
                      valueStyle={{ color: "#1890ff" }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12}>
                  <Card className="stat-card">
                    <Statistic
                      title="Conductores Activos"
                      value={stats.totalConductores}
                      prefix={<CarOutlined />}
                      valueStyle={{ color: "#52c41a" }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12}>
                  <Card className="stat-card">
                    <Statistic
                      title="Clientes EPS"
                      value={stats.totalClientes}
                      prefix={<TeamOutlined />}
                      valueStyle={{ color: "#722ed1" }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12}>
                  <Card className="stat-card">
                    <Statistic
                      title="Traslados Totales"
                      value={totalTraslados()}
                      prefix={<CalendarOutlined />}
                      valueStyle={{ color: "#fa8c16" }}
                    />
                  </Card>
                </Col>
              </Row>
            </Col>
            <Col xs={24} md={12} style={{ display: "flex" }}>
              <Card
                title="Top 5 Clientes EPS"
                className="chart-card top-clients-card"
                style={{ width: "100%" }}
              >
                <Table
                  className="top-clients-table"
                  columns={columnsTopClientes}
                  dataSource={topClientes}
                  pagination={false}
                  size="small"
                  rowKey="cliente"
                  showHeader={false}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={[24, 24]} style={{ marginTop: "24px" }}>
            <Col xs={24} md={12}>
              <Card title="Distribución por Estado" className="chart-card">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="estado" />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" fill="#8884d8">
                      {chartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[entry.estado]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title="Distribución Porcentual" className="chart-card">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomizedLabel}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="porcentaje"
                      nameKey="estado"
                    >
                      {chartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[entry.estado]}
                          name={entry.estado}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      formatter={(value, entry) => entry.payload.estado}
                      verticalAlign="bottom"
                      height={36}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>

          <Row gutter={[24, 24]} style={{ marginTop: "24px" }}>
            <Col xs={24} md={4}>
              <Card
                className="stat-card detailed-stat"
                data-status="completados"
              >
                <Statistic
                  title="Finalizados"
                  value={stats.trasladosCompletados}
                  prefix={<CheckCircleOutlined style={{ color: "#4d4d4d" }} />}
                  valueStyle={{ color: "#4d4d4d" }}
                />
                <div className="stat-percentage">
                  {chartData.find((d) => d.estado === "Finalizado")
                    ?.porcentaje || 0}
                  %
                </div>
              </Card>
            </Col>
            <Col xs={24} md={4}>
              <Card
                className="stat-card detailed-stat"
                data-status="cancelados"
              >
                <Statistic
                  title="Cancelados"
                  value={stats.trasladosCancelados}
                  prefix={<CloseCircleOutlined style={{ color: "#b10e1a" }} />}
                  valueStyle={{ color: "#b10e1a" }}
                />
                <div className="stat-percentage">
                  {chartData.find((d) => d.estado === "Cancelado")
                    ?.porcentaje || 0}
                  %
                </div>
              </Card>
            </Col>
            <Col xs={24} md={4}>
              <Card className="stat-card detailed-stat" data-status="fallidos">
                <Statistic
                  title="Fallidos"
                  value={stats.trasladosFallidos}
                  prefix={<WarningOutlined style={{ color: "#4a2c04" }} />}
                  valueStyle={{ color: "#4a2c04" }}
                />
                <div className="stat-percentage">
                  {chartData.find((d) => d.estado === "Fallido")?.porcentaje ||
                    0}
                  %
                </div>
              </Card>
            </Col>
            <Col xs={24} md={4}>
              <Card
                className="stat-card detailed-stat"
                data-status="programados"
              >
                <Statistic
                  title="Programados"
                  value={stats.trasladosProgramados}
                  prefix={<ClockCircleOutlined style={{ color: "#6c06b5" }} />}
                  valueStyle={{ color: "#6c06b5" }}
                />
                <div className="stat-percentage">
                  {chartData.find((d) => d.estado === "Programado")
                    ?.porcentaje || 0}
                  %
                </div>
              </Card>
            </Col>
            <Col xs={24} md={4}>
              <Card className="stat-card detailed-stat" data-status="en-curso">
                <Statistic
                  title="En Curso"
                  value={stats.trasladosEnCurso}
                  prefix={<CarOutlined style={{ color: "#FFBF00" }} />}
                  valueStyle={{ color: "#FFBF00" }}
                />
                <div className="stat-percentage">
                  {chartData.find((d) => d.estado === "En Curso")?.porcentaje ||
                    0}
                  %
                </div>
              </Card>
            </Col>
            <Col xs={24} md={4}>
              <Card className="stat-card detailed-stat" data-status="pendiente-validacion">
                <Statistic
                  title="Pendiente Validación"
                  value={stats.trasladosPendienteValidacion}
                  prefix={<ClockCircleOutlined style={{ color: "#fa8c16" }} />}
                  valueStyle={{ color: "#fa8c16" }}
                />
                <div className="stat-percentage">
                  {chartData.find((d) => d.estado === "Pendiente Validación")?.porcentaje ||
                    0}
                  %
                </div>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default Dashboard;
